import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Plus, X, Check, XCircle, Trash2, Users2, Calculator } from "lucide-react";
import { useAuth } from "../lib/auth";
import DestCityFilter from "../components/DestCityFilter";
import CohortOrganizerModal from "../components/CohortOrganizerModal";

const CUR_SYM = { USD:"$", EUR:"€", GBP:"£" };

const emptyTrip = () => ({
  name: "", date: "", departure_time: "", days: 1, currency: "USD",
  participants: [], cities: [], hotels: [], taxis: [], meals: [], notes: "",
});

export default function Trips() {
  const { user } = useAuth();
  const [trips, setTrips] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [taxis, setTaxis] = useState([]);
  const [projects, setProjects] = useState([]);
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyTrip());
  const [filter, setFilter] = useState({ destination: "", city: "" });
  const [organizerCohort, setOrganizerCohort] = useState(null);

  const load = async () => {
    const q = new URLSearchParams();
    if (filter.destination) q.set("destination", filter.destination);
    if (filter.city) q.set("city", filter.city);
    const [t, h, tx, pj, g] = await Promise.all([
      api.get(`/trips?${q.toString()}`), api.get("/hotels"), api.get("/taxis"),
      api.get("/projects"),
      api.get("/weekend-bookings/grouped-by-date").catch(() => ({ data: [] })),
    ]);
    setTrips(t.data); setHotels(h.data); setTaxis(tx.data); setProjects(pj.data);
    setGroups(g.data || []);
  };
  useEffect(() => { load(); }, [filter.destination, filter.city]);

  // Founder + BDOM (destination-scoped BDOM inherits the same access, so a
  // Jaipur BDOM can build/edit Ranthambore cohort sheets). Every backend
  // endpoint the modal calls already allows both roles, so this is purely a
  // UI gate.
  const isFounder = user.effective_role === "FOUNDER" || user.effective_role === "BDOM";

  const submit = async () => {
    if (!form.name) return toast.error("Trip name required");
    // Participants self-book from the portal — the staff planner no longer
    // assigns them manually. We still send `participants: []` so backend
    // pricing (meal × days × pax) uses a default of 1 pax.
    await api.post("/trips", { ...form, participants: [] });
    setForm(emptyTrip()); setOpen(false); await load(); toast.success("Trip submitted");
  };

  const approve = async (id, status) => {
    await api.post(`/trips/${id}/approval`, { status });
    await load();
    toast.success(status === "approved" ? "Approved" : "Rejected");
  };

  const addRow = (key, blank) => setForm(v => ({ ...v, [key]: [...v[key], { ...blank, _id: `${key}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` }] }));
  const updRow = (key, i, patch) => setForm(v => ({ ...v, [key]: v[key].map((x,idx) => idx===i ? {...x, ...patch}: x) }));
  const rmRow = (key, i) => setForm(v => ({ ...v, [key]: v[key].filter((_,idx) => idx !== i) }));

  const hotelTotal = form.hotels.reduce((s,h)=>s + (parseFloat(h.price_per_night||0) * parseInt(h.nights||1)), 0);
  const taxiTotal = form.taxis.reduce((s,t)=>s + parseFloat(t.price||0), 0);
  const mealTotal = form.meals.reduce((s,m)=>s + parseFloat(m.price_per_day||0), 0) * parseInt(form.days||1);
  const grand = hotelTotal + taxiTotal + mealTotal;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-semibold">Weekend Tour Organizer</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <DestCityFilter value={filter} onChange={setFilter} />
          <button onClick={()=>setOpen(true)} className="text-sm px-3 py-2 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold flex items-center gap-1.5"><Plus size={14}/> New trip</button>
        </div>
      </div>

      {groups.length > 0 && (
        <div className="aii-card p-4" data-testid="wkgroup-panel">
          <div className="flex items-center gap-2 mb-3">
            <Users2 size={16} className="text-brand"/>
            <h2 className="font-display font-semibold">Weekend cohorts by date</h2>
            <span className="text-xs text-[color:var(--aii-muted)]">Participants booked for the same weekend — plan shared hotel · taxi · meals in one shot.</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {groups.map((g,idx) => (
              <div key={`${g.start_date}-${g.destinations}-${idx}`}
                   role={isFounder ? "button" : undefined}
                   tabIndex={isFounder ? 0 : undefined}
                   onClick={() => isFounder && setOrganizerCohort(g)}
                   onKeyDown={(e) => { if (isFounder && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setOrganizerCohort(g); } }}
                   className={`rounded-md border border-[color:var(--aii-border)] bg-[color:var(--aii-bg)] p-3 ${isFounder ? "cursor-pointer hover:border-brand hover:shadow-sm transition" : ""}`}
                   data-testid={`wkgroup-${idx}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold">{g.destinations || "—"}</div>
                    <div className="text-xs text-[color:var(--aii-muted)]">{g.start_date || "Unscheduled"}{g.weekend_trips ? ` · ${g.weekend_trips}` : ""}</div>
                  </div>
                  {g.max_pax > 0 ? (
                    // Seats-remaining chip once max_pax is set. Amber inside 3
                    // of full, red if over-booked, green while there's plenty.
                    (() => {
                      const remain = Math.max(0, (g.max_pax || 0) - g.count);
                      const over = g.count > g.max_pax;
                      const cls = over ? "bg-red-100 text-red-700 border-red-300"
                                       : remain <= 3 ? "bg-amber-100 text-amber-800 border-amber-300"
                                       : "bg-[color:var(--aii-green-tint)] text-brand border-brand";
                      return (
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${cls}`}
                              data-testid={`wkgroup-seats-${idx}`}>
                          {g.count}/{g.max_pax} {over ? "OVER" : remain === 0 ? "FULL" : `${remain} left`}
                        </span>
                      );
                    })()
                  ) : (
                    <span className="pill pill-green">{g.count} pax</span>
                  )}
                </div>
                <ul className="mt-2 space-y-0.5 text-xs">
                  {g.participants.slice(0,6).map(p => (
                    <li key={p.id} className="flex items-center justify-between">
                      <span>{p.customer}</span>
                      {p.partner_name && <span className="text-[10px] text-[color:var(--aii-muted)] ml-2">via {p.partner_name}</span>}
                    </li>
                  ))}
                  {g.participants.length > 6 && <li className="text-[color:var(--aii-muted)]">+ {g.participants.length-6} more</li>}
                </ul>
                {isFounder && (
                  <div className="mt-2 pt-2 border-t border-[color:var(--aii-border)] text-[11px] text-brand font-semibold flex items-center gap-1">
                    <Calculator size={11}/> Open cost organizer →
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {open && (
        <div className="aii-card p-6 space-y-4">
          <div className="flex justify-between items-center"><h3 className="font-semibold">New trip plan</h3><button onClick={()=>setOpen(false)}><X size={16}/></button></div>
          <div className="grid sm:grid-cols-4 gap-3">
            <div><label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Trip name</label><input value={form.name} onChange={e=>setForm(v=>({...v,name:e.target.value}))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/></div>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Date</label><input type="date" value={form.date} onChange={e=>setForm(v=>({...v,date:e.target.value}))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/></div>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Departure</label><input type="time" value={form.departure_time} onChange={e=>setForm(v=>({...v,departure_time:e.target.value}))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/></div>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Days</label><input type="number" min="1" value={form.days} onChange={e=>setForm(v=>({...v,days:parseInt(e.target.value)||1}))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/></div>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Currency</label>
              <select value={form.currency} onChange={e=>setForm(v=>({...v,currency:e.target.value}))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
                {["USD","EUR","GBP","INR"].map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Cities</label>
            <input value={form.cities.join(", ")} onChange={e=>setForm(v=>({...v, cities: e.target.value.split(",").map(c=>c.trim()).filter(Boolean)}))} placeholder="Jaipur, Agra, Ranthambore" className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
            <p className="text-[10px] text-[color:var(--aii-muted)] mt-1">Once approved, participants self-book from their portal. You&rsquo;ll see the booked / skipped counts on the row below.</p>
          </div>

          <Section title="Hotels" onAdd={()=>addRow("hotels",{city:"",hotel:"",price_per_night:0,nights:1})}>
            {form.hotels.map((h,i)=>(
              <div key={h._id || `hotel-${i}`} className="grid grid-cols-12 gap-2 items-center">
                <select value={h.city} onChange={e=>updRow("hotels",i,{city:e.target.value})} className="col-span-3 px-2 py-1.5 border border-[color:var(--aii-border)] rounded bg-white text-sm">
                  <option value="">City</option>{hotels.map(x=><option key={x.id}>{x.name}</option>)}
                </select>
                <input value={h.hotel} onChange={e=>updRow("hotels",i,{hotel:e.target.value})} placeholder="Hotel name" className="col-span-4 px-2 py-1.5 border border-[color:var(--aii-border)] rounded bg-white text-sm"/>
                <input type="number" value={h.price_per_night} onChange={e=>updRow("hotels",i,{price_per_night:parseFloat(e.target.value)||0})} placeholder="Price/night" className="col-span-2 px-2 py-1.5 border border-[color:var(--aii-border)] rounded bg-white text-sm"/>
                <input type="number" value={h.nights} onChange={e=>updRow("hotels",i,{nights:parseInt(e.target.value)||1})} placeholder="Nights" className="col-span-2 px-2 py-1.5 border border-[color:var(--aii-border)] rounded bg-white text-sm"/>
                <button onClick={()=>rmRow("hotels",i)} className="col-span-1 text-[color:var(--aii-muted)] hover:text-red-600"><Trash2 size={14}/></button>
              </div>
            ))}
          </Section>

          <Section title="Taxis" onAdd={()=>addRow("taxis",{name:"",price:0})}>
            {form.taxis.map((t,i)=>(
              <div key={t._id || `taxi-${i}`} className="grid grid-cols-12 gap-2 items-center">
                <select value={t.name} onChange={e=>updRow("taxis",i,{name:e.target.value})} className="col-span-6 px-2 py-1.5 border border-[color:var(--aii-border)] rounded bg-white text-sm">
                  <option value="">Taxi</option>{taxis.map(x=><option key={x.id} value={x.name}>{x.name}{x.extra?` — ${x.extra}`:""}</option>)}
                </select>
                <input type="number" value={t.price} onChange={e=>updRow("taxis",i,{price:parseFloat(e.target.value)||0})} placeholder="Total price" className="col-span-5 px-2 py-1.5 border border-[color:var(--aii-border)] rounded bg-white text-sm"/>
                <button onClick={()=>rmRow("taxis",i)} className="col-span-1 text-[color:var(--aii-muted)] hover:text-red-600"><Trash2 size={14}/></button>
              </div>
            ))}
          </Section>

          <Section title="Meals" onAdd={()=>addRow("meals",{name:"",price_per_day:0})}>
            {form.meals.map((m,i)=>(
              <div key={m._id || `meal-${i}`} className="grid grid-cols-12 gap-2 items-center">
                <input value={m.name} onChange={e=>updRow("meals",i,{name:e.target.value})} placeholder="Meal name" className="col-span-6 px-2 py-1.5 border border-[color:var(--aii-border)] rounded bg-white text-sm"/>
                <input type="number" value={m.price_per_day} onChange={e=>updRow("meals",i,{price_per_day:parseFloat(e.target.value)||0})} placeholder="Price/day/person" className="col-span-5 px-2 py-1.5 border border-[color:var(--aii-border)] rounded bg-white text-sm"/>
                <button onClick={()=>rmRow("meals",i)} className="col-span-1 text-[color:var(--aii-muted)] hover:text-red-600"><Trash2 size={14}/></button>
              </div>
            ))}
          </Section>

          {/* Monument Ticket Fare — entry tickets for sights on the itinerary.
              Priced per unit (person) × how-many-people, so it scales cleanly
              with cohort size on the roll-up card below. */}
          <Section title="Monument Ticket Fare" onAdd={()=>addRow("monuments",{name:"",price_per_person:0,pax:1})}>
            {(form.monuments || []).map((m,i)=>(
              <div key={m._id || `mon-${i}`} className="grid grid-cols-12 gap-2 items-center">
                <input value={m.name} onChange={e=>updRow("monuments",i,{name:e.target.value})} placeholder="e.g. Amber Fort · Hawa Mahal" className="col-span-6 px-2 py-1.5 border border-[color:var(--aii-border)] rounded bg-white text-sm"/>
                <input type="number" value={m.price_per_person} onChange={e=>updRow("monuments",i,{price_per_person:parseFloat(e.target.value)||0})} placeholder="Price/person" className="col-span-3 px-2 py-1.5 border border-[color:var(--aii-border)] rounded bg-white text-sm"/>
                <input type="number" value={m.pax || 1} onChange={e=>updRow("monuments",i,{pax:parseInt(e.target.value)||1})} placeholder="Pax" className="col-span-2 px-2 py-1.5 border border-[color:var(--aii-border)] rounded bg-white text-sm"/>
                <button onClick={()=>rmRow("monuments",i)} className="col-span-1 text-[color:var(--aii-muted)] hover:text-red-600"><Trash2 size={14}/></button>
              </div>
            ))}
          </Section>

          <div className="bg-[color:var(--aii-green-tint)] rounded-md p-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div><div className="text-xs text-[color:var(--aii-text-2)]">Hotels</div><div className="font-display font-semibold text-lg">{CUR_SYM[form.currency]||form.currency}{hotelTotal.toFixed(0)}</div></div>
            <div><div className="text-xs text-[color:var(--aii-text-2)]">Taxis</div><div className="font-display font-semibold text-lg">{CUR_SYM[form.currency]||form.currency}{taxiTotal.toFixed(0)}</div></div>
            <div><div className="text-xs text-[color:var(--aii-text-2)]">Meals × {form.days} day(s) × 1 pax</div><div className="font-display font-semibold text-lg">{CUR_SYM[form.currency]||form.currency}{mealTotal.toFixed(0)}</div></div>
            <div><div className="text-xs text-[color:var(--aii-text-2)]">Grand total</div><div className="font-display font-bold text-2xl text-brand">{CUR_SYM[form.currency]||form.currency}{grand.toFixed(0)}</div></div>
          </div>

          <textarea value={form.notes} onChange={e=>setForm(v=>({...v,notes:e.target.value}))} placeholder="Notes for the founder…" className="w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm" rows={2}/>

          <div className="flex justify-end"><button onClick={submit} className="bg-brand text-white font-semibold px-6 py-2 rounded-md hover:bg-brand-dark">Submit for approval</button></div>
        </div>
      )}

      <div className="aii-card overflow-x-auto">
        <div className="overflow-x-auto">
          <table className="aii-table">
            <thead><tr><th>Trip</th><th>Date</th><th className="text-center">Booked</th><th className="text-center">Skipped</th><th>Days</th><th>Total</th><th>Status</th><th>Created</th><th></th></tr></thead>
            <tbody>
              {trips.length===0 && <tr><td colSpan={9} className="text-center py-10 text-[color:var(--aii-muted)]">No trips yet.</td></tr>}
              {trips.map(t=>(
                <tr key={t.id}>
                  <td className="font-medium">{t.name}</td>
                  <td>{t.date||"—"}</td>
                  <td className="text-center"><span className="pill pill-green" title={(t.booked_names||[]).join(", ") || "no bookings yet"}>{t.booked_count||0}</span></td>
                  <td className="text-center"><span className="pill pill-amber">{t.skipped_count||0}</span></td>
                  <td>{t.days}</td>
                  <td className="font-semibold text-brand">{CUR_SYM[t.currency]||t.currency}{(t.total||0).toLocaleString(undefined,{maximumFractionDigits:0})}</td>
                  <td><span className={`pill ${t.status==='approved'?'pill-green':t.status==='rejected'?'pill-red':'pill-amber'}`}>{t.status}</span></td>
                  <td className="text-xs text-[color:var(--aii-muted)]">{t.created_by}</td>
                  <td className="text-right">
                    {isFounder && t.status==='pending' && (
                      <div className="inline-flex gap-1">
                        <button onClick={()=>approve(t.id,"approved")} className="text-xs bg-brand text-white px-2 py-1 rounded flex items-center gap-1"><Check size={11}/> Approve</button>
                        <button onClick={()=>approve(t.id,"rejected")} className="text-xs bg-red-600 text-white px-2 py-1 rounded flex items-center gap-1"><XCircle size={11}/> Reject</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {organizerCohort && (
        <CohortOrganizerModal cohort={organizerCohort} onClose={() => setOrganizerCohort(null)} />
      )}
    </div>
  );
}

const Section = ({ title, children, onAdd }) => (
  <div className="border border-[color:var(--aii-border)] rounded-md p-3">
    <div className="flex justify-between items-center mb-2">
      <div className="text-sm font-semibold">{title}</div>
      <button onClick={onAdd} className="text-xs text-brand hover:underline flex items-center gap-1"><Plus size={12}/> Add</button>
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);
